"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/ui/icon";
import { customInstance } from "@/lib/api/client";
import { formatLongDate } from "@/lib/date/format-long-date";

interface PausedBannerProps {
  resumeDate: string | null;
}

export function PausedBanner({ resumeDate }: PausedBannerProps) {
  const queryClient = useQueryClient();
  const [resuming, setResuming] = useState(false);

  const formattedDate = formatLongDate(resumeDate) ?? "your next billing cycle";

  const handleResume = async () => {
    setResuming(true);
    try {
      await customInstance("/api/billing/resume", { method: "POST" });
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
    } catch {
      setResuming(false);
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#ffaf53]/20 bg-[#ffaf53]/5 px-4 py-2.5">
      <Icon name="IconClock" size={16} className="text-[#ffaf53] shrink-0" />
      <p className="text-sm text-[#ffaf53]/90 flex-1">
        Your subscription is paused. Billing resumes on{" "}
        <span className="font-medium text-[#ffaf53]">{formattedDate}</span>.
      </p>
      <button
        type="button"
        onClick={handleResume}
        disabled={resuming}
        className="h-7 px-3 rounded-md text-xs font-medium bg-[#ffaf53]/10 border border-[#ffaf53]/20 text-[#ffaf53] hover:bg-[#ffaf53]/20 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
      >
        {resuming ? (
          <Icon
            name="IconLoadingCircle"
            size={12}
            className="animate-spin"
          />
        ) : (
          "Resume Now"
        )}
      </button>
    </div>
  );
}
